import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api';

// Helper: get local date string YYYY-MM-DD (no UTC shift)
function getLocalDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getDefaultTermin(kategoria) {
  const today = new Date();
  if (kategoria === 'dzis') return getLocalDateString(today);
  if (kategoria === 'jutro') {
    const t = new Date(today); t.setDate(t.getDate() + 1);
    return getLocalDateString(t);
  }
  if (kategoria === 'tydzien') {
    const t = new Date(today); t.setDate(t.getDate() + 7);
    return getLocalDateString(t);
  }
  if (kategoria === 'miesiac') {
    const t = new Date(today); t.setDate(t.getDate() + 30);
    return getLocalDateString(t);
  }
  return '';
}

export default function TaskModal({ task, onClose, onSave, groups, locations = [], users = [], currentUser, selectedLocation, defaultCategory = 'dzis' }) {
  const initialKategoria = task?.kategoria || defaultCategory;
  const [formData, setFormData] = useState({
    tytul: task?.tytul || '',
    opis: task?.opis || '',
    status: task?.status || 'nowe',
    priorytet: task?.priorytet || 'normalny',
    kategoria: initialKategoria,
    termin_wykonania: task?.termin_wykonania || getDefaultTermin(initialKategoria),
    godzina_wykonania: task?.godzina_wykonania || '',
    grupa_id: task?.grupa_id || '',
    location_id: task?.location_id || selectedLocation || '',
    user_id: task?.user_id || '',
    is_private: task?.is_private || false,
    przypisany_do: task?.przypisany_do || '',
    notatki: task?.notatki || '',
    utworzyl: task?.utworzyl || currentUser?.login || 'system'
  });
  const [saving, setSaving] = useState(false);
  const [availableUsers, setAvailableUsers] = useState(users);

  // Fetch users for selected location
  useEffect(() => {
    const url = formData.location_id
      ? `/tasks/users?location_id=${formData.location_id}`
      : `/tasks/users`;

    api.get(url)
      .then(response => {
        if (response.data.success) {
          setAvailableUsers(response.data.data || []);
        }
      })
      .catch(err => console.error('Error fetching users:', err));
  }, [formData.location_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tytul.trim()) return;

    setSaving(true);
    try {
      const url = task ? `/tasks/${task.id}` : '/tasks';
      const response = task
        ? await api.put(url, formData)
        : await api.post(url, formData);

      const data = response.data;

      if (data.success) {
        onSave();
      } else {
        toast.error('Błąd zapisu: ' + (data.error || data.message || 'Nieznany błąd'));
      }
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error('Błąd zapisu: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '6px 8px',
    border: '1px solid var(--border)',
    borderRadius: '3px',
    fontSize: '12px',
    height: '30px',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--text-muted)',
    marginBottom: '3px'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '0.5rem'
    }}>
      <div style={{
        background: "var(--card-bg)",
        borderRadius: '6px',
        width: '450px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
      }}>
        {/* Header */}
        <div style={{
          padding: '0.75rem 1rem',
          border: task
            ? '1px solid var(--orange)'
            : '1px solid var(--success)',
          color: task ? 'var(--orange)' : 'var(--success)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <div style={{
            width: '2rem',
            height: '2rem',
            background: 'transparent',
            borderRadius: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1rem'
          }}>
            {task ? '✏️' : '➕'}
          </div>
          <div style={{ flex: 1 }}>
            <h5 style={{ margin: 0, fontWeight: 600, fontSize: '13px' }}>
              {task ? 'Edytuj zadanie' : 'Nowe zadanie'}
            </h5>
            <p style={{ margin: 0, fontSize: '10px', opacity: 0.9 }}>
              {task ? 'Wprowadź zmiany w zadaniu' : 'Dodaj nowe zadanie do listy'}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              fontSize: '1.25rem',
              color: 'var(--text-secondary)',
              padding: '0.25rem',
              lineHeight: 1
            }}
          >
            {'✕'}
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '1rem' }}>
          {/* Title */}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Tytuł *</label>
            <input
              type="text"
              value={formData.tytul}
              onChange={(e) => setFormData({ ...formData, tytul: e.target.value })}
              style={inputStyle}
              placeholder="Co trzeba zrobić?"
              autoFocus
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Opis</label>
            <textarea
              value={formData.opis}
              onChange={(e) => setFormData({ ...formData, opis: e.target.value })}
              style={{ ...inputStyle, minHeight: '50px', height: 'auto', resize: 'vertical' }}
              placeholder="Dodatkowe szczegóły..."
            />
          </div>

          {/* Details section */}
          <div style={{
            marginBottom: '0.75rem',
            padding: '0.75rem',
            background: 'var(--bg)',
            borderRadius: '5px',
            border: '1px solid var(--border)'
          }}>
            <h6 style={{ margin: '0 0 0.75rem 0', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '12px' }}>
              {'📋'} Szczegóły zadania
            </h6>

            {/* Row: Priority & Category */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label style={labelStyle}>Priorytet</label>
                <select
                  value={formData.priorytet}
                  onChange={(e) => setFormData({ ...formData, priorytet: e.target.value })}
                  style={inputStyle}
                >
                  <option value="niski">{'🟢'} Niski</option>
                  <option value="normalny">{'🔵'} Normalny</option>
                  <option value="wysoki">{'🟠'} Wysoki</option>
                  <option value="pilny">{'🔴'} Pilny</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Kategoria czasowa</label>
                <select
                  value={formData.kategoria}
                  onChange={(e) => {
                    const newKategoria = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      kategoria: newKategoria,
                      termin_wykonania: getDefaultTermin(newKategoria)
                    }));
                  }}
                  style={inputStyle}
                >
                  <option value="dzis">{'📅'} Dziś</option>
                  <option value="jutro">{'📆'} Jutro</option>
                  <option value="tydzien">{'📋'} Ten tydzień</option>
                  <option value="miesiac">{'🗓️'} Ten miesiąc</option>
                  <option value="kiedys">{'♾️'} Kiedyś</option>
                </select>
              </div>
            </div>

            {/* Row: Date & Time */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label style={labelStyle}>Termin wykonania</label>
                <input
                  type="date"
                  value={formData.termin_wykonania ? formData.termin_wykonania.substring(0, 10) : ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, termin_wykonania: e.target.value }))}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Godzina</label>
                <input
                  type="time"
                  value={formData.godzina_wykonania}
                  onChange={(e) => setFormData({ ...formData, godzina_wykonania: e.target.value })}
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Row: Group */}
            <div>
              <label style={labelStyle}>Grupa</label>
              <select
                value={formData.grupa_id}
                onChange={(e) => setFormData({ ...formData, grupa_id: e.target.value })}
                style={inputStyle}
              >
                <option value="">-- Bez grupy --</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.is_global ? '🌐 ' : ''}{g.nazwa}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Delegation section */}
          <div style={{
            marginBottom: '0.75rem',
            padding: '0.75rem',
            background: 'var(--info-light)',
            borderRadius: '5px',
            border: '1px solid var(--info-light)'
          }}>
            <h6 style={{ margin: '0 0 0.75rem 0', fontWeight: 600, color: 'var(--info)', fontSize: '12px' }}>
              {'📍'} Delegowanie zadania
            </h6>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label style={labelStyle}>Lokalizacja</label>
                <select
                  value={formData.location_id}
                  onChange={(e) => setFormData({ ...formData, location_id: e.target.value, przypisany_do: '' })}
                  style={inputStyle}
                >
                  <option value="">{'🌐'} Wszystkie lokalizacje (globalne)</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{'📍'} {loc.nazwa}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Przypisz do użytkownika</label>
                <select
                  value={formData.przypisany_do}
                  onChange={(e) => setFormData({ ...formData, przypisany_do: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">{'👥'} Dla wszystkich</option>
                  {availableUsers.map(u => (
                    <option key={u.id} value={u.nazwa}>
                      {'👤'} {u.nazwa} {u.location_nazwa ? `(${u.location_nazwa})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                fontSize: '12px',
                color: 'var(--text-secondary)'
              }}>
                <input
                  type="checkbox"
                  checked={formData.is_private}
                  onChange={(e) => setFormData({ ...formData, is_private: e.target.checked })}
                  style={{ width: '16px', height: '16px' }}
                />
                <span>{'🔒'} Zadanie prywatne (widoczne tylko dla przypisanej osoby)</span>
              </label>
            </div>

            {/* Visibility info */}
            <div style={{
              padding: '8px',
              background: formData.is_private ? 'var(--purple-light)' : !formData.location_id ? 'var(--success-light)' : 'var(--success-light)',
              borderRadius: '4px',
              fontSize: '11px',
              color: formData.is_private ? 'var(--purple)' : !formData.location_id ? 'var(--success)' : 'var(--success)'
            }}>
              {formData.is_private
                ? `🔒 Zadanie prywatne - widoczne tylko dla ${formData.przypisany_do || 'przypisanego użytkownika'}`
                : !formData.location_id
                  ? formData.przypisany_do
                    ? `🌐 Zadanie globalne przypisane do: ${formData.przypisany_do}`
                    : '🌐 Zadanie globalne - widoczne we wszystkich lokalizacjach dla wszystkich'
                  : formData.przypisany_do
                    ? `📍 Zadanie dla ${locations.find(l => l.id === parseInt(formData.location_id))?.nazwa || 'lokalizacji'} przypisane do: ${formData.przypisany_do}`
                    : `📍 Zadanie dla całej lokalizacji: ${locations.find(l => l.id === parseInt(formData.location_id))?.nazwa || ''}`
              }
            </div>
          </div>

          {/* Status (for edit) */}
          {task && (
            <div style={{
              marginBottom: '0.75rem',
              padding: '0.75rem',
              background: 'var(--info-light)',
              borderRadius: '5px',
              border: '1px solid var(--info-light)'
            }}>
              <h6 style={{ margin: '0 0 0.5rem 0', fontWeight: 600, color: 'var(--info)', fontSize: '12px' }}>
                {'🔄'} Status zadania
              </h6>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                style={inputStyle}
              >
                <option value="nowe">{'⚪'} Nowe</option>
                <option value="w_trakcie">{'🔵'} W trakcie</option>
                <option value="zakonczone">{'✅'} Zakończone</option>
                <option value="anulowane">{'❌'} Anulowane</option>
              </select>
            </div>
          )}

          {/* Notes */}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Notatki</label>
            <textarea
              value={formData.notatki}
              onChange={(e) => setFormData({ ...formData, notatki: e.target.value })}
              style={{ ...inputStyle, minHeight: '40px', height: 'auto', resize: 'vertical' }}
              placeholder="Dodatkowe notatki..."
            />
          </div>

          {/* Buttons */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            paddingTop: '0.75rem',
            borderTop: '1px solid var(--border)'
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '6px 12px',
                border: '1px solid var(--text-muted)', color: 'var(--text-muted)',
                fontWeight: 500,
                fontSize: '12px'
              }}
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={saving || !formData.tytul.trim()}
              style={{
                flex: 2,
                padding: '6px 12px',
                border: task
                  ? '1px solid var(--orange)'
                  : '1px solid var(--success)',
                color: task ? 'var(--orange)' : 'var(--success)',
                fontWeight: 500,
                fontSize: '12px',
                opacity: (saving || !formData.tytul.trim()) ? 0.6 : 1
              }}
            >
              {saving ? '⏳ Zapisywanie...' : (task ? '💾 Zapisz zmiany' : '➕ Dodaj zadanie')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
